import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, logout, getUserId } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./MCQStudentPage.css";

export default function MCQStudentPage() {
  const [mcqs, setMcqs] = useState([]);
  const [completedMap, setCompletedMap] = useState({});
  const [pageLoading, setPageLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewType, setViewType] = useState("pending");

  const navigate = useNavigate();
  const studentId = getUserId();

  const formatStartTime = (dateString) =>
    new Date(dateString).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  /* ---------- CHECK COMPLETED ---------- */
  const checkCompletedMCQs = useCallback(
    async (mcqList) => {
      try {
        const requests = mcqList.map((mcq) =>
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/mcq-submissions/student/${studentId}/mcq/${mcq._id}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          )
            .then((res) => res.json())
            .then((data) => {
              if (Array.isArray(data) && data.length > 0) {
                const s = data[0];
                return {
                  mcqId: mcq._id,
                  completed: true,
                  score: s.score || 0,
                  total: s.totalMarks || 0,
                  isTabSwitch: s.isTabSwitch || false,
                  submittedAt: s.createdAt || null,
                };
              }
              return { mcqId: mcq._id, completed: false, score: 0, total: 0, isTabSwitch: false, submittedAt: null };
            })
        );
        const results = await Promise.all(requests);
        const statusMap = {};
        results.forEach((r) => { statusMap[r.mcqId] = r; });
        setCompletedMap(statusMap);
      } catch (err) {
        console.error("Failed to check MCQ completion", err);
      }
    },
    [studentId]
  );

  /* ---------- FETCH ---------- */
  const fetchMCQs = useCallback(async () => {
    setPageLoading(true);
    const t0 = Date.now();
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mcqs/mcqs-student`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ studentId }),
        }
      );
      if (res.status === 401 || res.status === 403) { logout(); navigate("/login"); return; }
      const data = await res.json();
      const mcqList = Array.isArray(data) ? data : [];
      setMcqs(mcqList);
      await checkCompletedMCQs(mcqList);
    } catch (err) {
      console.error("Failed to load MCQs", err);
      setMcqs([]);
    } finally {
      const remaining = Math.max(3000 - (Date.now() - t0), 0);
      setTimeout(() => setPageLoading(false), remaining);
    }
  }, [studentId, navigate, checkCompletedMCQs]);

  useEffect(() => { fetchMCQs(); }, [fetchMCQs]);

  /* ---------- FILTERS ---------- */
  const filtered = mcqs
    .filter((mcq) => {
      const q = search.toLowerCase();
      return mcq.topic?.toLowerCase().includes(q) || mcq.category?.toLowerCase().includes(q);
    })
    .filter((mcq) => {
      const s = completedMap[mcq._id];
      return viewType === "pending" ? !s?.completed : s?.completed;
    })
    .sort((a, b) => {
      if (viewType !== "completed") return 0;
      return new Date(completedMap[b._id]?.submittedAt || 0) - new Date(completedMap[a._id]?.submittedAt || 0);
    });

  const totalCompleted = mcqs.filter((m) => completedMap[m._id]?.completed).length;
  const totalPending   = mcqs.length - totalCompleted;

  if (pageLoading) {
    return (
      <HumanLoader loadingText="Preparing MCQ tests" successText="Ready to start" duration={2000} />
    );
  }

  return (
    <div className="mspage">

      {/* ── TOP BAR ── */}
      <div className="mspage-topbar">
        <div className="mspage-title-block">
          <h1 className="mspage-title">MCQ Assessments</h1>
          <p className="mspage-subtitle">Select a test below to begin your assessment</p>
        </div>

        <div className="mspage-search">
          <svg className="mspage-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by topic or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div className="mspage-stats">
        <div className="msstat">
          <span className="msstat-value">{mcqs.length}</span>
          <span className="msstat-label">Total Tests</span>
        </div>
        <div className="msstat-divider" />
        <div className="msstat">
          <span className="msstat-value msstat-pending">{totalPending}</span>
          <span className="msstat-label">Pending</span>
        </div>
        <div className="msstat-divider" />
        <div className="msstat">
          <span className="msstat-value msstat-done">{totalCompleted}</span>
          <span className="msstat-label">Completed</span>
        </div>
        <div className="msstat-divider" />
        <div className="msstat">
          <span className="msstat-value">
            {mcqs.length > 0 ? Math.round((totalCompleted / mcqs.length) * 100) : 0}%
          </span>
          <span className="msstat-label">Progress</span>
        </div>
      </div>

      {/* ── TOGGLE ── */}
      <div className="mspage-toggle">
        <button
          className={viewType === "pending" ? "mstab active" : "mstab"}
          onClick={() => setViewType("pending")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          Pending
          {totalPending > 0 && <span className="mstab-count">{totalPending}</span>}
        </button>
        <button
          className={viewType === "completed" ? "mstab active" : "mstab"}
          onClick={() => setViewType("completed")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Completed
          {totalCompleted > 0 && <span className="mstab-count mstab-count-done">{totalCompleted}</span>}
        </button>
      </div>

      {/* ── GRID ── */}
      {filtered.length === 0 ? (
        <div className="mspage-empty">
          <div className="msempty-icon">
            {viewType === "pending" ? (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#334155"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#334155"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
          <p className="msempty-title">
            {viewType === "pending" ? "All caught up!" : "Nothing completed yet"}
          </p>
          <p className="msempty-sub">
            {viewType === "pending"
              ? "You have no pending MCQ tests at the moment."
              : "Complete a test to see your results here."}
          </p>
        </div>
      ) : (
        <div className="ms-grid">
          {filtered.map((mcq) => {
            const sub         = completedMap[mcq._id];
            const isCompleted = sub?.completed;
            const now         = new Date();
            const startAt     = new Date(mcq.startTime || "2025-01-01T00:00:00");
            const isLocked    = now < startAt;
            const pct         = isCompleted && sub.total > 0
              ? Math.round((sub.score / sub.total) * 100) : 0;
            const circumference = 2 * Math.PI * 20; // r=20

            return (
              <div key={mcq._id} className={`ms-card ${isCompleted ? "ms-card-done" : ""}`}>

                {/* top accent line */}
                <div className={`ms-card-accent ${isCompleted ? "accent-done" : isLocked ? "accent-locked" : "accent-active"}`} />

                {/* header row */}
                <div className="ms-card-head">
                  <div className="ms-card-meta">
                    {mcq.category && <span className="ms-cat-chip">{mcq.category}</span>}
                    {isCompleted && (
                      <span className="ms-status-chip ms-status-done">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Completed
                      </span>
                    )}
                    {!isCompleted && isLocked && (
                      <span className="ms-status-chip ms-status-locked">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0110 0v4"/>
                        </svg>
                        Locked
                      </span>
                    )}
                    {!isCompleted && !isLocked && (
                      <span className="ms-status-chip ms-status-open">Open</span>
                    )}
                  </div>

                  {/* score ring — only when completed */}
                  {isCompleted && (
                    <div className="ms-score-ring">
                      <svg width="52" height="52" viewBox="0 0 52 52">
                        <circle cx="26" cy="26" r="20" fill="none" stroke="#1e293b" strokeWidth="4"/>
                        <circle
                          cx="26" cy="26" r="20" fill="none"
                          stroke={pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444"}
                          strokeWidth="4"
                          strokeDasharray={circumference}
                          strokeDashoffset={circumference - (pct / 100) * circumference}
                          strokeLinecap="round"
                          transform="rotate(-90 26 26)"
                        />
                      </svg>
                      <span className="ms-ring-pct">{pct}%</span>
                    </div>
                  )}
                </div>

                {/* title */}
                <h3 className="ms-card-title">{mcq.topic}</h3>

                {/* info row */}
                <div className="ms-card-info">
                  <span className="ms-info-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    {mcq.questions?.length || 0} Questions
                  </span>
                  {mcq.duration && (
                    <span className="ms-info-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {mcq.duration} min
                    </span>
                  )}
                </div>

                {/* tab-switch warning */}
                {isCompleted && sub.isTabSwitch && (
                  <div className="ms-tabswitch-warn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    Tab switch detected
                  </div>
                )}

                {/* score bar — only when completed */}
                {isCompleted && (
                  <div className="ms-score-bar-wrap">
                    <div className="ms-score-bar-track">
                      <div
                        className="ms-score-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444",
                        }}
                      />
                    </div>
                    <span className="ms-score-label">{sub.score} / {sub.total}</span>
                  </div>
                )}

                {/* action button */}
                <div className="ms-card-footer">
                  {isCompleted ? (
                    <button className="ms-btn ms-btn-done" disabled>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Submitted
                    </button>
                  ) : isLocked ? (
                    <button className="ms-btn ms-btn-locked" disabled>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                      Opens {formatStartTime(mcq.startTime)}
                    </button>
                  ) : (
                    <button className="ms-btn ms-btn-start" onClick={() => navigate(`/mcqs/test/${mcq._id}`)}>
                      Start Test
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* ghost cards to keep grid aligned */}
          {Array.from({ length: (3 - (filtered.length % 3)) % 3 }).map((_, i) => (
            <div key={`ghost-${i}`} className="ms-card ms-ghost" />
          ))}
        </div>
      )}
    </div>
  );
}
